import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUrlNumber, useUrlString, useUrlList, useUrlBool } from '../../hooks/useUrlState';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Card, Tag, Typography, Input, Select, Button, Space, Tooltip, FilterResetButton, message, StatCard, ActionMenu, Avatar, type TagTone } from '../../ui';
import { Plus, Pencil, Ban, Phone, MessageCircle, Filter, RotateCcw, CalendarClock, Users, UserCheck, UserMinus, LogIn, User as UserIcon } from 'lucide-react';
import { request } from '../../api/client';
import { usePermission, usePermissionChecker, useHasRole } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { useServerSort } from '../../hooks/useServerSort';
import { DataTable, DataTableColumn } from '../../components/DataTable';
import { UserFormModal } from './UserFormModal';
import { DeactivateUserModal } from './DeactivateUserModal';
import { AttendanceUserDrawer, type AttUser } from '../ffa/AttendanceUserDrawer';
import { formatIst, formatIstDate } from '../../lib/time';
import { STATE_NAME } from '../../constants/indianStates';
import { LANGUAGE_NAME } from '../../constants/languages';
import { facetOptions, renderFacetOption, type Facet } from '../../lib/facetOptions';
import { HoverCascader, type CascOpt } from '../../components/HoverCascader';
import { CopyValue } from '../../components/CopyValue';

const STATE_LABEL = STATE_NAME;
const ROLE_COLOR: Record<string, TagTone> = {
  SUPER_ADMIN: 'red', ADMIN: 'red', ACCOUNTS: 'gold',
  SALES_HEAD: 'purple', SALES_PERSON: 'blue', RETAILER: 'cyan', FARMER: 'green',
};
const STATUS_COLOR: Record<string, TagTone> = { active: 'green', suspended: 'red', locked: 'gold' };
const opt = facetOptions;
// Language options resolve the raw code (en/mr) to its name, keeping the facet count.
const langOpt = (arr: Facet[]) => facetOptions(arr, (o) => LANGUAGE_NAME[String(o.value)] ?? String(o.value).toUpperCase());

// Staff cascader (Role → Team) labels/order, and territory-tree walkers for the hierarchical territory filter.
const STAFF_ROLE_LABELS: Record<string, string> = {
  SALES_HEAD: 'Sales Heads', SALES_PERSON: 'Sales Persons', ACCOUNTS: 'Accountants',
  WAREHOUSE_MANAGER: 'Warehouse Managers', ADMIN: 'Admins', SUPER_ADMIN: 'Super Admins',
};
// Ordered by the app's standard role hierarchy (senior → junior), matching the Roles page and role labels
// elsewhere — so the Role / Team filter reads Super Admins → Admins → Accountants → Sales Heads → Sales Persons
// → Warehouse Managers, not field-staff-first with Super Admins buried at the bottom.
const STAFF_ROLE_ORDER = ['SUPER_ADMIN', 'ADMIN', 'ACCOUNTS', 'SALES_HEAD', 'SALES_PERSON', 'WAREHOUSE_MANAGER'];
function findTreeNode(nodes: any[] | undefined, id: string): any {
  for (const n of nodes ?? []) { if (String(n.id) === String(id)) return n; const f = findTreeNode(n.children, id); if (f) return f; }
  return null;
}
function subtreeIds(node: any): string[] { return [String(node.id), ...(node.children ?? []).flatMap(subtreeIds)]; }
function ancestorIds(nodes: any[] | undefined, id: string, trail: string[] = []): string[] | null {
  for (const n of nodes ?? []) {
    if (String(n.id) === String(id)) return trail;
    const r = ancestorIds(n.children, id, [...trail, String(n.id)]);
    if (r) return r;
  }
  return null;
}

/**
 * Location for a Warehouse Manager — the site(s) they run.
 *
 * A WHM holds no territory (the user form offers them warehouses, not a territory picker), so this
 * column used to render whatever stale `territoryId` was left on the row from a previous role: a
 * manager running Pune, Mumbai, Delhi and Bengaluru read "Nagpur · Maharashtra", a place nothing
 * on the account linked him to. The posting comes with the row (`warehouses`), primary site first.
 *
 * Distinct places, not one line per warehouse — two Pune sites are one location, and the point of
 * the cell is WHERE, not how many rows the Warehouse Managers tab holds. The full list, sites named,
 * is a hover away.
 */
type WhPosting = { code: string; name: string; city: string | null; stateCode: string | null; isPrimary: boolean };
function WarehousePosting({ posts }: { posts?: WhPosting[] }) {
  const where = (w: WhPosting) =>
    [w.city, w.stateCode ? (STATE_LABEL.get(w.stateCode) ?? w.stateCode) : null].filter(Boolean).join(' · ');
  const places = [...new Set((posts ?? []).map(where).filter(Boolean))];
  if (!places.length) return <span className="text-subtle">—</span>;
  const label = places.length > 1 ? `${places[0]} +${places.length - 1}` : places[0];
  return (
    // `inline` — the cell truncates, and an inline-flex trigger would defeat its ellipsis.
    <Tooltip
      inline
      label={
        <span className="whitespace-pre-line">
          {(posts ?? []).map((w) => [w.code, where(w)].filter(Boolean).join(' · ')).join('\n')}
        </span>
      }
    >
      <span>{label}</span>
    </Tooltip>
  );
}

export function UsersPage({ embedded }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [page, setPage] = useUrlNumber('page', 1);
  const [pageSize, setPageSize] = useUrlNumber('pageSize', 25);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 400);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const hasPerm = usePermissionChecker();
  const canCreate = hasPerm('USER.CREATE');
  /**
   * EDITING A STAFF RECORD IS SUPER-ADMIN-ONLY, and the server says so.
   *
   * Every writable field on a user is an access fact — the mobile is the OTP login, the role is the
   * permission set, the reporting line decides whose work a manager can read — so `PATCH /users/:id`
   * now refuses them all below Super Admin (see the backend's `user-field-policy.ts`). `USER.EDIT`
   * still opens these screens for an Admin; it no longer writes. Gating the pencil on the same rule
   * keeps an Admin out of a form that would 403 on submit.
   */
  const canEdit = useHasRole('SUPER_ADMIN');
  const canDelete = hasPerm('USER.DELETE');
  const canViewAttendance = hasPerm('ATTENDANCE.VIEW');
  const reactivate = useConfirmedMutation({ title: "Reactivate this user?", content: "They can sign in again. Territories and customers are not restored automatically — assign them again if needed.", okText: "Yes, reactivate" }, {
    mutationFn: (id: string) => request({ url: `/users/${id}/reactivate`, method: 'PATCH' }),
    onSuccess: () => { message.success('User reactivated'); qc.invalidateQueries({ queryKey: ['users'] }); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed to reactivate'),
  });

  // ---- Filters (server-side) ----
  const [statusF, setStatusF] = useUrlList('status');
  const [langF, setLangF] = useUrlList('lang');
  const [staffPath, setStaffPath] = useUrlList('staff');  // Role → Team cascader
  const [terrPath, setTerrPath] = useUrlList('terr');  // Territory cascader
  // Server-side column sort (backend ORDER BY over the full result set, not just this page).
  const { sortState, onSortChange, sort } = useServerSort({ onChange: () => setPage(1) });
  // Accounts created but never used — the "Never signed in" tile is the only way to filter for these.
  const [neverOnly, setNeverOnly] = useUrlBool('never');
  // Deactivated staff. A server-side flag, not a status facet — see the DTO comment.
  const [inactiveOnly, setInactiveOnly] = useUrlBool('inactive');

  // Nested territory tree — drives the Territory cascader + its hierarchical (ancestors+descendants) expansion.
  // /territories/tree needs TERRITORY.VIEW. Accounts holds USER.VIEW (so it opens this tab) but not
  // that, so asking anyway logged a 403 and the cascader stayed empty; hidden below instead.
  const canViewTerritories = usePermission('TERRITORY.VIEW');
  const { data: terrTree } = useQuery({
    queryKey: ['territories-tree'],
    queryFn: () => request<any[]>({ url: '/territories/tree' }),
    enabled: canViewTerritories,
  });

  const params = useMemo(() => {
    const p: Record<string, any> = { page, pageSize };
    if (search.trim()) p.search = search.trim();
    // Role filter → role code (flat; no manager/team drill-down).
    const staffLeaf = staffPath?.length ? staffPath[staffPath.length - 1] : undefined;
    if (staffLeaf?.startsWith('role:')) p.role = staffLeaf.slice(5);
    // Territory cascader → coversTerritoryId = the picked node + its ancestors + descendants (by assignment).
    const terrLeaf = terrPath?.length ? terrPath[terrPath.length - 1] : undefined;
    if (terrLeaf) {
      const node = findTreeNode(terrTree, terrLeaf);
      const ids = [...new Set([...(ancestorIds(terrTree, terrLeaf) ?? []), ...(node ? subtreeIds(node) : [terrLeaf])])];
      if (ids.length) p.coversTerritoryId = ids.join(',');
    }
    if (statusF.length) p.status = statusF.join(',');
    if (langF.length) p.languagePref = langF.join(',');
    if (neverOnly) p.neverLoggedIn = 'true';
    if (inactiveOnly) p.inactiveOnly = 'true';
    if (sort) p.sort = sort;
    return p;
  }, [page, pageSize, search, staffPath, terrPath, statusF, langF, neverOnly, inactiveOnly, terrTree, sort]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['users', params],
    queryFn: () => request<any>({ url: '/users', params }),
    placeholderData: keepPreviousData,
  });
  const { data: facets } = useQuery({
    queryKey: ['users', 'filter-options'],
    queryFn: () => request<any>({ url: '/users/filter-options' }),
  });

  /**
   * Deep link from a UserLink elsewhere in the app (`/people?tab=users&userId=123`): fetch that one
   * user and open their record, instead of dropping the visitor on an unfiltered list of everyone.
   * Read once and then stripped from the URL, so a refresh or a later manual close doesn't reopen it.
   */
  const [deepLinked, setDeepLinked] = useState(false);
  useEffect(() => {
    const wanted = searchParams.get('userId');
    if (!wanted || deepLinked) return;
    setDeepLinked(true);
    const next = new URLSearchParams(searchParams);
    next.delete('userId');
    setSearchParams(next, { replace: true });
    (async () => {
      try {
        const u = await request<any>({ url: `/users/${wanted}` });
        const rec = (u as any)?.data ?? u;
        if (!rec) return;
        // Narrow the list to them as well, so the person is still findable behind the modal — and so
        // a viewer who cannot edit (who gets no modal) still lands on the right row.
        setSearchInput(rec.fullName ?? rec.mobile ?? '');
        if (canEdit) { setEditing(rec); setFormOpen(true); }
      } catch {
        message.error('That staff member could not be opened');
      }
    })();
  }, [searchParams, deepLinked, canEdit, setSearchParams]);

  // Plain role filter — a flat list of roles, no team/manager drill-down (Sales Persons filters by role only).
  const staffOptions = useMemo<CascOpt[]>(() => {
    const roleCodes = new Set((facets?.roles ?? []).map((r: any) => String(r.value)));
    return STAFF_ROLE_ORDER.filter((c) => roleCodes.has(c)).map((c) => ({
      value: `role:${c}`,
      label: STAFF_ROLE_LABELS[c] ?? c,
    }));
  }, [facets]);
  // Territory cascader options: state → district → beat (the single India zone is collapsed; multi-zone shows zones).
  const territoryOptions = useMemo<CascOpt[]>(() => {
    const toOpt = (n: any): CascOpt => ({ value: String(n.id), label: n.name, children: n.children?.length ? n.children.map(toOpt) : undefined });
    const roots = terrTree ?? [];
    const start = (roots.length === 1 && roots[0].level === 'zone') ? (roots[0].children ?? []) : roots;
    return start.map(toOpt);
  }, [terrTree]);

  const terrName = useMemo(() => new Map<string, string>((facets?.territories ?? []).map((t: any) => [String(t.value), String(t.label)] as [string, string])), [facets]);
  // Manager (reports-to) names, resolved from the same facet the "Under X" filter is built from.
  const managerName = useMemo(() => new Map<string, string>((facets?.managers ?? []).map((m: any) => [String(m.value), String(m.label)] as [string, string])), [facets]);

  // Resolve each territory → its own name + the state it belongs to, so the Location
  // column reads consistently as "Area · State" (or just the state for a state node).
  const { data: territories } = useQuery({
    queryKey: ['territories-for-users'],
    // Scope-aware list — full-access roles (incl. Accounts, who holds USER.VIEW and so opens this tab)
    // get every territory, a rep gets their own. The master `/territories` tree needs TERRITORY.VIEW,
    // which Accounts does not hold, so the Location column used to fall back to raw ids after a 403.
    queryFn: () => request<any[]>({ url: '/territory-assignments/me/territories' }),
  });
  type TerrInfo = { name: string; stateCode: string | null; level: string };
  const terrInfo = useMemo(
    () => new Map<string, TerrInfo>(
      (territories ?? []).map((t: any) => [String(t.id), { name: t.name, stateCode: t.stateCode, level: t.level }] as [string, TerrInfo]),
    ),
    [territories],
  );
  const territoryLabel = (id: any): string => {
    const t = terrInfo.get(String(id));
    if (!t) return terrName.get(String(id)) ?? String(id);
    const state = t.stateCode ? (STATE_LABEL.get(t.stateCode) ?? t.stateCode) : null;
    if (!state || t.level === 'state' || t.name === state) return state ?? t.name;
    return `${t.name} · ${state}`;
  };

  const [deactivating, setDeactivating] = useState<any | null>(null);
  const [attUser, setAttUser] = useState<AttUser | null>(null);

  const activeFilters = (staffPath?.length ? 1 : 0) + (terrPath?.length ? 1 : 0) + statusF.length + langF.length + (neverOnly ? 1 : 0) + (inactiveOnly ? 1 : 0) + (search ? 1 : 0);
  const clearFilters = () => {
    setSearchInput(''); setStaffPath([]); setTerrPath([]); setStatusF([]); setLangF([]); setPage(1);
      setNeverOnly(false); setInactiveOnly(false);
  };

  const kpis: Record<string, number> = facets?.kpis ?? {};
  // Every headline number is also a way in — a KPI you cannot click is a dead end.
  const isActiveOnly = statusF.length === 1 && statusF[0] === 'active';
  const noFilters = !statusF.length && !neverOnly && !inactiveOnly && !staffPath?.length && !langF.length && !terrPath?.length && !search;

  const columns: DataTableColumn<any>[] = [
    { key: 'photo', title: '', dataIndex: 'profilePhotoUrl', width: 56, alwaysVisible: true,
      render: (v: string) => <Avatar size={32} src={v} fallback={<UserIcon size={16} />} /> },
    { key: 'fullName', title: 'Name', dataIndex: 'fullName', sortable: true, alwaysVisible: true, width: 220, ellipsis: true },
    // Click-to-copy: a drag across the digits pans the table, so the number was unselectable.
    { key: 'mobile', title: 'Mobile', dataIndex: 'mobile', sortable: true, alwaysVisible: true, width: 150,
      render: (v: string) => v && <CopyValue value={v} label="Mobile"><code>{v}</code></CopyValue> },
    { key: 'email', title: 'Email', dataIndex: 'email', sortable: true, width: 220, ellipsis: true,
      render: (v: string) => v && <a href={`mailto:${v}`} onClick={(e) => e.stopPropagation()}>{v}</a> },
    { key: 'role', title: 'Role', dataIndex: ['role', 'code'], width: 190, sortable: true,
      // One role per user (single-role policy) — show the single role badge.
      render: (v: string) => v && <Tag tone={ROLE_COLOR[v] ?? 'neutral'} style={{ margin: 0 }}>{v}</Tag> },
    { key: 'status', title: 'Status', dataIndex: 'status', width: 110, sortable: true,
      render: (v: string) => <Tag tone={STATUS_COLOR[v] ?? 'neutral'}>{v}</Tag> },
    { key: 'languagePref', title: 'Lang', dataIndex: 'languagePref', width: 90, defaultVisible: false,
      render: (v: string) => v && <Tag>{v.toUpperCase()}</Tag> },
    { key: 'location', title: 'Location', dataIndex: 'territoryId', width: 200, ellipsis: true, alwaysVisible: true,
      render: (v: any, r: any) => (v ? territoryLabel(v) : <WarehousePosting posts={r.warehouses} />) },
    { key: 'reportsTo', title: 'Reports to', dataIndex: 'reportsTo', width: 180, ellipsis: true,
      render: (v: any) => (v && managerName.get(String(v))) || <span style={{ color: '#bbb' }}>—</span> },
    { key: 'lastLoginAt', title: 'Last Login', dataIndex: 'lastLoginAt', width: 160, sortable: true,
      render: (v: string) => (v ? formatIst(v) : <Tag>Never</Tag>) },
    {
      // Field roles are one-device-per-user, so "why can't they sign in on the new handset?" is a
      // standing support question the register could never answer.
      key: 'device', title: 'Device', width: 120,
      render: (_: any, r: any) => (r.deviceId
        ? (
          <Tooltip label={`${r.deviceId} — click to copy`}>
            <span
              role="button"
              tabIndex={0}
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard?.writeText(String(r.deviceId))
                  .then(() => message.success('Device ID copied'))
                  .catch(() => message.error('Could not copy — the full ID is in the tooltip'));
              }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') (e.currentTarget as HTMLElement).click(); }}
            >
              <Tag tone="green">Bound</Tag>
            </span>
          </Tooltip>
        )
        : <Tag>Not bound</Tag>) },
    // The raw identifier, for when you need to read it out or diff two handsets. Off by default —
    // it is a long opaque string that earns its column width only when you are chasing a binding.
    // A device id exists to be pasted into a support ticket, and it is too long to read off screen.
    { key: 'deviceId', title: 'Device ID', dataIndex: 'deviceId', width: 240, defaultVisible: false, ellipsis: true,
      render: (v: string) => (v
        ? <CopyValue value={v} label="Device ID" truncate><code className="type-label">{v}</code></CopyValue>
        : <span className="text-subtle">—</span>) },
    {
      key: 'lock', title: 'Sign-in', width: 130, defaultVisible: false,
      render: (_: any, r: any) => {
        const locked = r.lockedUntil && new Date(r.lockedUntil) > new Date();
        if (locked) return <Tooltip label={`Locked until ${formatIst(r.lockedUntil)}`}><span><Tag tone="red">Locked</Tag></span></Tooltip>;
        return Number(r.failedAttempts) > 0
          ? <Tag tone="gold">{r.failedAttempts} failed</Tag>
          : <span className="text-subtle">OK</span>;
      } },
    { key: 'createdAt', title: 'Created', dataIndex: 'createdAt', width: 120, sortable: true, render: (v: string) => formatIstDate(v) },
    { key: 'languageCol', title: 'Language', dataIndex: 'languagePref', width: 110, defaultVisible: false,
      render: (v: string) => (v ? <Tag>{LANGUAGE_NAME[v] ?? v.toUpperCase()}</Tag> : <span className="text-subtle">—</span>) },
    { key: 'email2', title: 'Email', dataIndex: 'email', width: 220, ellipsis: true, defaultVisible: false,
      render: (v: string) => v || <span className="text-subtle">—</span> },
    {
      /**
       * Edit inline, the rest behind the kebab. This row carried up to six icon buttons plus an
       * empty placeholder span kept purely so the columns lined up — a sure sign the cluster had
       * outgrown the space. Words in a menu also make "Reactivate" and "Deactivate" unmistakable,
       * which two similar-looking glyphs never were.
       */
      key: 'actions', title: '', alwaysVisible: true, width: 104, fixed: 'right',
      render: (_: any, r: any) => {
        const showAtt = canViewAttendance && (r.role?.code === 'SALES_PERSON' || r.role?.code === 'SALES_HEAD');
        const items = [
          { key: 'call', label: 'Call', icon: <Phone size={16} />, onSelect: () => { window.location.href = `tel:${r.mobile}`; } },
          { key: 'wa', label: 'WhatsApp', icon: <MessageCircle size={16} />, onSelect: () => window.open(`https://wa.me/${(r.mobile ?? '').replace('+', '')}`, '_blank', 'noopener') },
          showAtt && {
            key: 'att', label: 'Attendance history', icon: <CalendarClock size={16} />, dividerBefore: true,
            onSelect: () => setAttUser({ id: String(r.id), name: r.fullName, role: r.role?.name ?? r.role?.code }),
          },
          r.status === 'active' && canDelete && {
            key: 'deact', label: 'Deactivate', icon: <Ban size={16} />, danger: true, dividerBefore: true,
            onSelect: () => setDeactivating(r),
          },
          r.status !== 'active' && canDelete && {
            key: 'react', label: 'Reactivate', icon: <RotateCcw size={16} />, dividerBefore: true,
            onSelect: () => reactivate.mutate(r.id),
          },
        ].filter(Boolean) as any[];
        return (
          <div className="flex w-full items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
            {canEdit && (<Tooltip label="Edit"><Button variant="text" size="sm" aria-label="Edit" icon={<Pencil size={16} />} onClick={() => { setEditing(r); setFormOpen(true); }} /></Tooltip>)}
            <ActionMenu items={items} label="More actions" />
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}>Users</Typography.Title>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canCreate && (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(null); setFormOpen(true); }}>Add user</Button>
          )}
        </div>
      </div>
      {/* Headline numbers over the whole staff register, from the server — not the page on screen. */}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Staff" tone="brand" icon={<Users className="size-4" />}
          value={(kpis.total ?? 0).toLocaleString('en-IN')}
          hint={`${(kpis.fieldStaff ?? 0).toLocaleString('en-IN')} in the field`}
          active={noFilters}
          onClick={clearFilters}
        />
        <StatCard
          label="Active" tone="success" icon={<UserCheck className="size-4" />}
          value={(kpis.active ?? 0).toLocaleString('en-IN')}
          hint={`${(kpis.activeWeek ?? 0).toLocaleString('en-IN')} signed in this week`}
          active={isActiveOnly}
          onClick={() => { const on = !isActiveOnly; setStatusF(on ? ['active'] : []); if (on) { setInactiveOnly(false); setNeverOnly(false); } setPage(1); }}
        />
        <StatCard
          label="Deactivated" tone={kpis.inactive ? 'danger' : 'neutral'} icon={<UserMinus className="size-4" />}
          value={(kpis.inactive ?? 0).toLocaleString('en-IN')}
          hint="Cannot sign in"
          active={inactiveOnly}
          onClick={() => { const on = !inactiveOnly; setInactiveOnly(on); setStatusF([]); if (on) setNeverOnly(false); setPage(1); }}
        />
        <StatCard
          label="Never signed in" tone={kpis.neverLoggedIn ? 'warning' : 'neutral'} icon={<LogIn className="size-4" />}
          value={(kpis.neverLoggedIn ?? 0).toLocaleString('en-IN')}
          hint="Account created, never used"
          active={neverOnly}
          onClick={() => { const on = !neverOnly; setNeverOnly(on); if (on) { setInactiveOnly(false); setStatusF([]); } setPage(1); }}
        />
      </div>
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input placeholder="Search name, email, mobile…" aria-label="Search name, email, mobile" prefix={<Filter size={16} style={{ color: '#aaa' }} />}
            className="w-80" value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(1); }} />
          <HoverCascader options={staffOptions} value={staffPath} onChange={(v) => { setStaffPath(v ?? []); setPage(1); }} placeholder="Role" minWidth={200} />
          <Select mode="multiple" allowClear placeholder="Status" style={{ minWidth: 140 }} maxTagCount={1}
            optionRender={renderFacetOption}
            value={statusF} onChange={(v) => { setStatusF(v); setPage(1); }} options={opt(facets?.statuses)} />
          <Select mode="multiple" allowClear placeholder="Language" style={{ minWidth: 130 }} maxTagCount={1}
            optionRender={renderFacetOption}
            value={langF} onChange={(v) => { setLangF(v); setPage(1); }} options={langOpt(facets?.languages)} />
          {canViewTerritories && (
            <HoverCascader options={territoryOptions} value={terrPath} onChange={(v) => { setTerrPath(v ?? []); setPage(1); }} placeholder="Territory" minWidth={180} />
          )}
          {activeFilters > 0 && (
            <FilterResetButton size="md" count={activeFilters} onClick={clearFilters} />
          )}
        </div>

        <DataTable
          storageKey="users-list"
          rowKey="id"
          loading={isLoading || isFetching}
          dataSource={data?.data ?? []}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          scroll={{ x: 1300 }}
          /*
           * A row opens the person, it does not open a FORM.
           *
           * Clicking a name here used to pop the edit modal, so the ordinary act of looking someone
           * up put you in a form you had to cancel out of — and it did nothing at all for a reader
           * who could not edit. The row now goes to their read-first detail page, where every field
           * edits IN PLACE for a Super Admin; the pencil in the actions cell still opens the fuller
           * form directly for anyone who came here to change territories or warehouses.
           */
          onRow={(r) => ({
            onClick: () => navigate(`/staff/${r.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: page,
            pageSize,
            total: data?.meta?.total ?? 0,
            showSizeChanger: true,
            showTotal: (t) => `${t.toLocaleString('en-IN')} users`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>

      <UserFormModal open={formOpen} record={editing} onClose={() => { setFormOpen(false); setEditing(null); }} />
      <DeactivateUserModal open={!!deactivating} user={deactivating} onClose={() => setDeactivating(null)} />
      <AttendanceUserDrawer user={attUser} onClose={() => setAttUser(null)} />
    </div>
  );
}
